import React, { useState } from "react";
import {
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  Platform,
  type StyleProp,
  type ViewStyle,
  type TextStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColor } from "@/hooks/useThemeColor";
import { BrandColors, Spacing, Fonts } from "@/constants/ThemeColours";

/**
 * Standard semantic labels for 1 to 5 star ratings.
 */
export const DEFAULT_RATING_LABELS: Record<number, string> = {
  1: "1 - Bad",
  2: "2 - Poor",
  3: "3 - Average",
  4: "4 - Good",
  5: "5 - Excellent",
};

/**
 * Interface for ThemedRating component properties.
 */
export interface IThemedRatingProps {
  /**
   * Current rating value (0 indicates unselected, 1 to maxRating).
   * @default 0
   */
  value: number;

  /**
   * Callback triggered when a rating is selected.
   */
  onChange?: (rating: number) => void;

  /**
   * Maximum rating scale (number of stars).
   * @default 5
   */
  maxRating?: number;

  /**
   * Size of the star icons in pixels.
   * @default 20
   */
  size?: number;

  /**
   * Whether to display the text label.
   * @default true
   */
  showLabel?: boolean;

  /**
   * Whether to display the rating description text (e.g. "1 - Bad").
   * @default false
   */
  showDescription?: boolean;
  layout?: "row" | "column";

  /**
   * Prefix label displayed before the rating description.
   * @default ""
   */
  label?: string;

  /**
   * Mapping of rating numbers to descriptive labels.
   * Defaults to DEFAULT_RATING_LABELS.
   */
  labels?: Record<number, string>;

  /**
   * Text displayed when no rating has been selected yet.
   * @default "Select a rating"
   */
  unselectedLabel?: string;

  /**
   * Active star fill color.
   * Defaults to BrandColors.sungoldOrange.hex.
   */
  activeColor?: string;

  /**
   * Inactive star outline color.
   * Defaults to theme border color.
   */
  inactiveColor?: string;

  /**
   * Whether the rating is read-only (non-interactive).
   * @default false
   */
  readOnly?: boolean;

  /**
   * Whether the rating control is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * Optional custom style for the root container.
   */
  style?: StyleProp<ViewStyle>;

  /**
   * Optional custom style for the label text.
   */
  labelStyle?: StyleProp<TextStyle>;

  /**
   * Optional test ID for automation.
   */
  testID?: string;
}

/**
 * Reusable, accessible star rating component adhering to Memories UX Standards.
 * Handles button hover states, dynamic hover preview, unselected initial state,
 * and high-contrast theme border feedback.
 *
 * @param {IThemedRatingProps} props - ThemedRating component properties.
 * @returns {React.JSX.Element} The rendered rating control.
 */
export function ThemedRating({
  value = 0,
  onChange,
  maxRating = 5,
  size = 20,
  showLabel = true,
  showDescription = false,
  layout,
  label = "",
  labels = DEFAULT_RATING_LABELS,
  unselectedLabel = "Select a rating (optional)",
  activeColor,
  inactiveColor,
  readOnly = false,
  disabled = false,
  style,
  labelStyle,
  testID,
}: IThemedRatingProps): React.JSX.Element {
  const [hoveredStar, setHoveredStar] = useState<number | null>(null);

  const themeTextColor = useThemeColor({}, "text");
  const themeBorderColor = useThemeColor({}, "border");
  const hoverBorderColor = useThemeColor({}, "buttonHoverBorder");
  const hoverBgColor = useThemeColor({}, "buttonSecondary");
  const subtextColor = useThemeColor({}, "tabIconDefault");

  const effectiveActiveColor = activeColor || BrandColors.sungoldOrange.hex;
  const effectiveInactiveColor = inactiveColor || themeBorderColor;

  const isInteractive = !disabled && !readOnly;
  const displayedRating = hoveredStar !== null ? hoveredStar : value;

  const getRatingDescription = (ratingVal: number): string => {
    if (ratingVal === 0) {
      return unselectedLabel;
    }
    return labels[ratingVal] || `${ratingVal}/${maxRating}`;
  };

  const handleStarSelect = (ratingVal: number): void => {
    if (!isInteractive) return;
    if (value === ratingVal) {
      onChange?.(0);
    } else {
      onChange?.(ratingVal);
    }
  };

  const starsArray = Array.from({ length: maxRating }, (_, idx) => idx + 1);

  // If layout is explicitly specified, use it. Otherwise, if there is a label without description, default to row layout.
  const isRowLayout = layout === "row" || (!layout && Boolean(label) && !showDescription);

  return (
    <View
      style={[
        styles.container,
        isRowLayout && styles.containerRow,
        style,
      ]}
      testID={testID}
    >
      {showLabel && (label || showDescription) ? (
        <View style={[styles.labelRow, isRowLayout && styles.labelRowInline]}>
          {label ? <Text style={[styles.labelText, { color: themeTextColor }, labelStyle]}>{label}</Text> : null}
          {showDescription ? (
            <Text
              style={[
                styles.ratingHighlight,
                { color: displayedRating > 0 ? effectiveActiveColor : subtextColor },
              ]}
            >
              {getRatingDescription(displayedRating)}
            </Text>
          ) : null}
        </View>
      ) : null}

      <View
        style={styles.starsRow}
        {...Platform.select({
          web: {
            onMouseLeave: () => {
              if (isInteractive) {
                setHoveredStar(null);
              }
            },
          } as any,
          default: {},
        })}
      >
        {starsArray.map((starNumber) => {
          const isActive = starNumber <= displayedRating;
          const isThisStarHovered = hoveredStar === starNumber;

          return (
            <TouchableOpacity
              key={starNumber}
              activeOpacity={0.7}
              disabled={!isInteractive}
              onPress={() => handleStarSelect(starNumber)}
              onHoverIn={() => {
                if (isInteractive) setHoveredStar(starNumber);
              }}
              onHoverOut={() => {
                if (isInteractive && hoveredStar === starNumber) setHoveredStar(null);
              }}
              {...Platform.select({
                web: {
                  onMouseEnter: () => {
                    if (isInteractive) setHoveredStar(starNumber);
                  },
                  onMouseLeave: () => {
                    if (isInteractive && hoveredStar === starNumber) setHoveredStar(null);
                  },
                } as any,
                default: {},
              })}
              style={[
                styles.starButton,
                {
                  width: size <= 18 ? size + 6 : size + 8,
                  height: size <= 18 ? size + 6 : size + 8,
                  borderColor: isThisStarHovered && isInteractive ? hoverBorderColor : "transparent",
                  backgroundColor: isThisStarHovered && isInteractive ? hoverBgColor : "transparent",
                },
                Platform.OS === "web" && ({
                  cursor: isInteractive ? "pointer" : "default",
                  transition: "border-color 0.15s ease, background-color 0.15s ease, transform 0.1s ease",
                  transform: isThisStarHovered && isInteractive ? "scale(1.1)" : "scale(1)",
                } as any),
              ]}
              accessibilityRole="button"
              accessibilityLabel={`Rate ${starNumber} of ${maxRating} stars`}
              accessibilityState={{
                selected: starNumber === value,
                disabled: !isInteractive,
              }}
            >
              <Ionicons
                name={isActive ? "star" : "star-outline"}
                size={size}
                color={isActive ? effectiveActiveColor : effectiveInactiveColor}
              />
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginVertical: Spacing.one,
  },
  containerRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginVertical: 2,
  },
  labelRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: Spacing.one,
  },
  labelRowInline: {
    marginBottom: 0,
  },
  labelText: {
    fontSize: 14,
    fontFamily: Fonts?.sans || "System",
    fontWeight: "600",
  },
  ratingHighlight: {
    fontSize: 14,
    fontFamily: Fonts?.sans || "System",
    fontWeight: "700",
  },
  starsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  starButton: {
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 6,
    borderWidth: 1.5,
  },
});
